import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Close, Delete, People } from "@mui/icons-material";
import SavingOverlay from "../SavingOverlay";
import { Toast } from "../Toast";
import { useToast } from "../../hooks/useToast";
import {
  professionalTeamService,
  TeamPatientItem,
} from "../../services/professionalTeamService";
import { colors } from "../../theme/designSystem";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamTitle: string;
}

// Pacientes atendidos pela equipe. Semântica do Bubble: com a flag desligada a
// equipe atende todos os pacientes da entidade; ligada, só os da lista.
// Cada mudança (flag, inclusão, remoção) é gravada na hora (escrita dupla).
const TeamPatientsModal: React.FC<Props> = ({ isOpen, onClose, teamId, teamTitle }) => {
  const { toast, showToast, hideToast } = useToast();
  const [specificPatients, setSpecificPatients] = useState(false);
  const [linked, setLinked] = useState<TeamPatientItem[]>([]);
  const [available, setAvailable] = useState<TeamPatientItem[]>([]);
  const [selected, setSelected] = useState<TeamPatientItem | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!teamId) return;
    setBusy(true);
    try {
      const data = await professionalTeamService.getPatients(teamId);
      setSpecificPatients(data.specificPatients);
      setLinked(data.linked);
      setAvailable(data.available);
    } catch (error) {
      console.error("Erro ao carregar pacientes da equipe:", error);
      showToast("Erro ao carregar os pacientes da equipe.", "error");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, teamId]);

  const errorMessage = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { description?: string } } })?.response
      ?.data?.description || fallback;

  const handleToggleSpecific = async (checked: boolean) => {
    if (busy) return;
    setBusy(true);
    try {
      await professionalTeamService.setSpecificPatients(teamId, checked);
      await load();
    } catch (error: unknown) {
      showToast(
        errorMessage(error, "Erro ao alterar a configuração de pacientes."),
        "error"
      );
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    if (busy || !selected) return;
    setBusy(true);
    try {
      await professionalTeamService.addPatient(teamId, selected.patientId);
      setSelected(null);
      await load();
    } catch (error: unknown) {
      showToast(
        errorMessage(error, "Erro ao incluir o paciente na equipe."),
        "error"
      );
      setBusy(false);
    }
  };

  const handleRemove = async (item: TeamPatientItem) => {
    if (busy) return;
    setBusy(true);
    try {
      await professionalTeamService.removePatient(teamId, item.patientId);
      await load();
    } catch (error: unknown) {
      showToast(
        errorMessage(error, "Erro ao remover o paciente da equipe."),
        "error"
      );
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (!busy) onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", position: "relative", overflow: "hidden" },
      }}
    >
      <SavingOverlay open={busy} message="Processando..." />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <People />
          {teamTitle} / Pacientes
        </Box>
        <IconButton
          onClick={onClose}
          disabled={busy}
          sx={{
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={specificPatients}
              onChange={(e) => handleToggleSpecific(e.target.checked)}
              disableRipple
              sx={{
                color: colors.primary,
                "&.Mui-checked": { color: colors.primary },
              }}
            />
          }
          label="Equipe atende pacientes específicos"
        />
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          {specificPatients
            ? "A equipe atende somente os pacientes da lista abaixo. Cada mudança é gravada imediatamente."
            : "Com a opção desligada, a equipe atende todos os pacientes da entidade. Ligue-a para montar uma lista específica."}
        </Typography>

        {specificPatients && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: 2,
              }}
            >
              <Autocomplete
                options={available}
                value={selected}
                onChange={(_, value) => setSelected(value)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option.patientId === value.patientId
                }
                noOptionsText="Nenhum paciente disponível"
                disabled={busy}
                size="small"
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar paciente"
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  />
                )}
              />
              <Button
                onClick={handleAdd}
                disabled={busy || !selected}
                variant="contained"
                sx={{
                  textTransform: "none",
                  backgroundColor: colors.primary,
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: colors.primary, opacity: 0.9 },
                }}
              >
                Adicionar
              </Button>
            </Box>

            <Box
              sx={{
                border: `1px solid ${colors.backgroundAlt}`,
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  p: 1,
                  borderBottom: `1px solid ${colors.backgroundAlt}`,
                  color: colors.textSecondary,
                }}
              >
                Pacientes da equipe ({linked.length})
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: "auto", p: 0.5 }}>
                {linked.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ color: colors.textSecondary, p: 1.5, textAlign: "center" }}
                  >
                    Nenhum paciente na lista.
                  </Typography>
                ) : (
                  linked.map((item) => (
                    <Box
                      key={item.patientId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        borderRadius: "6px",
                        "&:hover": { backgroundColor: colors.background },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Tooltip title="Remover da equipe">
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(item)}
                          sx={{ color: "#dc3545" }}
                        >
                          <Delete sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: "1rem 1.5rem" }}>
        <Button
          onClick={onClose}
          disabled={busy}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Fechar
        </Button>
      </DialogActions>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Dialog>
  );
};

export default TeamPatientsModal;
